import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ReactEcharts from 'echarts-for-react';
import merge from 'lodash.merge';
import { mean } from 'mathjs';
import { Div, Text } from '@pinpoint/design-kit';

import { traitsPropType, stylePropType } from 'src/PropTypes';
import { Style1 } from 'src/styles';

const oceanLegend = [
  { facet: 'Openness', facetRange: 'Traditional', color: '#C635A8' },
  { facet: 'Conscientiousness', facetRange: 'Conscientious', color: '#3AA6E8' },
  { facet: 'Extraversion', facetRange: 'Introverted', color: '#00C08B' },
  { facet: 'Agreeableness', facetRange: 'Agreeable', color: '#C2D500' },
  { facet: 'Neuroticism', facetRange: 'Emotionally Stable', color: '#FFBA00' },
];

class DeepOcean extends Component {
  constructor(props) {
    super(props);
    const { showRange, sortByFacet } = this.props;
    this.state = {
      showRange,
      sortByFacet,
    };
  }

  render() {
    const { labels, series, onMouseover, options, style, formatter, title } = this.props;
    const { showRange, sortByFacet } = this.state;
    const seriesList = [];
    for (let i = 0; i < series.length; i += 1) {
      const clonedData = labels
        .slice()
        .filter((datum) => datum.oceanColor && datum.oceanPosition)
        .map(({ highRangeName, lowRangeName, trait, oceanColor, oceanPosition }, index) =>
          showRange
            ? {
                name: series[i].values[index] < 50 ? lowRangeName : highRangeName,
                value: 50 + Math.abs(series[i].values[index] - 50),
                trait,
                range: series[i].values[index] < 50 ? lowRangeName : highRangeName,
                oceanPosition,
                itemStyle: {
                  color: oceanColor,
                },
              }
            : {
                name: trait,
                value: series[i].values[index],
                trait,
                oceanPosition,
                itemStyle: {
                  color: oceanColor,
                },
              }
        );
      if (sortByFacet) {
        clonedData.sort((a, b) => a.oceanPosition - b.oceanPosition);
      } else {
        clonedData.sort((a, b) => b.value - a.value);
      }
      seriesList.push({
        name: series[i].name,
        type: 'pie',
        ...style.DeepOcean.series,
        label: {
          ...style.DeepOcean.series.label,
          formatter,
        },
        data: clonedData,
      });
    }
    const buttonElements = [
      {
        type: 'group',
        top: 40,
        left: 20,
        width: 102,
        height: 32,
        onclick: () => {
          this.setState((prevState) => ({
            showRange: !prevState.showRange,
          }));
        },
        children: [
          {
            type: 'rect',
            shape: {
              x: 0,
              y: 0,
              width: 102,
              height: 32,
              r: 4,
            },
            style: {
              fill: '#0D79AF',
            },
          },
          {
            type: 'text',
            left: 'center',
            top: 'center',
            style: {
              text: showRange ? 'Show Traits' : 'Show Ranges',
              x: 0,
              y: 0,
              fill: '#FFF',
            },
          },
        ],
      },
      {
        type: 'group',
        top: 40,
        left: 20 + 102 + 5,
        width: 102,
        height: 32,
        onclick: () => {
          this.setState((prevState) => ({
            sortByFacet: !prevState.sortByFacet,
          }));
        },
        children: [
          {
            type: 'rect',
            shape: {
              x: 0,
              y: 0,
              width: 102,
              height: 32,
              r: 4,
            },
            style: {
              fill: '#0D79AF',
            },
          },
          {
            type: 'text',
            left: 'center',
            top: 'center',
            style: {
              text: sortByFacet ? 'Sort by Value' : 'Sort By Facet',
              x: 0,
              y: 0,
              fill: '#FFF',
            },
          },
        ],
      },
    ];
    /*
    const oceanElements = oceanLegend
      .map((item, index) => {
        const oceanValues = [];
        labels
          .forEach((label, i) => {
            if (label.factor === item.facet) {
              oceanValues.push(series[0].values[i]);
            }
          });
        const average = mean(...oceanValues);
        return {
          type: 'group',
          top: 104 + 30 * index,
          left: 20,
          children: [
            {
              type: 'rect',
              shape: {
                x: 0,
                y: 0,
                width: 24,
                height: 24,
                r: 4,
              },
              style: {
                fill: item.color,
              },
            },
            {
              type: 'text',
              left: 34,
              top: 6,
              style: {
                text: showRange ? item.facetRange : item.facet,
              },
            },
            {
              type: 'text',
              left: 164,
              top: 6,
              style: {
                text: showRange ? (100 - average).toFixed(1) : average.toFixed(1),
              },
            },
          ],
        };
      });
    */
    const chartOption = {
      title: {
        text: title,
        ...style.global.title,
      },
      tooltip: {
        ...style.DeepOcean.tooltip,
      },
      graphic: {
        elements: [...buttonElements],
      },
      toolbox: {
        ...style.global.toolbox,
      },
      series: seriesList,
    };
    const mergedOptions = merge(chartOption, options);
    return (
      <>
        <Div w="100%" h="calc(100% - 2.5rem)">
          <ReactEcharts
            ref={(element) => {
              const instance = element ? element.getEchartsInstance() : null;
              if (instance) {
                instance.on('mouseover', 'series', (params) => {
                  const {
                    data: { trait },
                  } = params;
                  const index = labels.findIndex((label) => label.trait === trait);
                  const values = series.map((_series) => _series.values[index]);
                  onMouseover({ label: labels[index], values });
                });
              }
            }}
            style={{
              width: '100%',
              height: '100%',
            }}
            option={mergedOptions}
          />
        </Div>
        <Div w="100%" d="flex" justify="space-between" m={{ x: { lg: '2rem', m: '1rem', xs: '0.25rem' } }} h="1.5rem">
          {oceanLegend.map((item) => {
            const oceanValues = [];
            labels.forEach((label, i) => {
              if (label.factor === item.facet) {
                oceanValues.push(series[0].values[i]);
              }
            });
            const average = mean(...oceanValues);
            return (
              <Div d="flex" align="center">
                <Div
                  w="1.5rem"
                  h="1.5rem"
                  rounded="sm"
                  style={{
                    backgroundColor: item.color,
                  }}
                  m={{ r: '0.5rem' }}
                />
                <Text m={{ r: '0.5rem' }} textSize="body">
                  {showRange ? item.facetRange.toUpperCase() : item.facet.toUpperCase()}
                </Text>
                <Text textSize="body">{showRange ? (100 - average).toFixed(1) : average.toFixed(1)}</Text>
              </Div>
            );
          })}
        </Div>
      </>
    );
  }
}

DeepOcean.propTypes = {
  title: PropTypes.string,
  labels: traitsPropType.isRequired,
  series: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      values: PropTypes.arrayOf(PropTypes.number),
    })
  ).isRequired,
  style: stylePropType,
  options: PropTypes.shape({}),
  formatter: PropTypes.func,
  showRange: PropTypes.bool,
  onMouseover: PropTypes.func,
  sortByFacet: PropTypes.bool,
};

DeepOcean.defaultProps = {
  title: '',
  style: Style1,
  options: {},
  formatter: (params) => `${params.name} ${params.value.toFixed(1)}`,
  showRange: false,
  sortByFacet: false,
  onMouseover: () => null,
};

export default DeepOcean;
